import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { datadogRum } from '@datadog/browser-rum';
import { EnvironmentConfigService } from './environment-config.service';
import { UserInfoResultDto } from '../_models/user-info-result-dto';
import packageInfo from '../../../package.json';

// ---- Module-level constants ----------------------------------------------

/**
 * Soft cap on the size of any single string attribute attached to a RUM error
 * (Datadog's per-attribute limit is ~1 KiB; we leave headroom for surrounding
 * structure). Applied by sanitizeBackendField when truncating long bodies.
 */
const MAX_BACKEND_FIELD_LEN = 500;

/**
 * URL substrings that mark an endpoint as auth-adjacent. For these endpoints
 * we drop the backend body text from the RUM event to prevent account
 * enumeration via short, distinguishable error messages
 * (e.g. "user not found" vs "invalid password"). Status + URL still reach
 * RUM, so aggregate auth-failure monitoring still works.
 */
const AUTH_PATH_PATTERNS = [
  '/login',
  '/token',
  '/forgot-password',
  '/reset-password'
];

/**
 * Legacy IE/Edge ActiveX-style errors emitted by older WebView environments
 * (typically from browser-notification COM bridges and similar shims).
 * Message format: `Object Not Found Matching Id:<N>, MethodName:<name>, ParamCount:<N>`.
 * Not actionable from RUM — comes from the browser-host integration, not our code.
 */
const IE_EDGE_ACTIVEX_PATTERN = /Object Not Found Matching Id:\d+, MethodName:/;

@Injectable({
  providedIn: 'root'
})
export class DatadogService {
  constructor(private envConfig: EnvironmentConfigService) {}

  initialize(): void {
    datadogRum.init({
      applicationId: 'd1f56c27-43f8-4015-9f25-40c04696032c',
      clientToken: 'pub7877008d4e43695a2ebca09e7cc85a76',
      site: 'us5.datadoghq.com',
      service: 'resident-ui',
      env: this.envConfig.currentUiEnvironment,
      version: packageInfo.version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      defaultPrivacyLevel: 'mask-user-input',
      // Enable RUM-APM correlation by injecting trace headers into requests to *.ciranet.com APIs
      allowedTracingUrls: [/^https:\/\/.*\.ciranet\.com/],
      beforeSend: (event) => {
        if (event.type !== 'error') return true;

        // Drop console-source duplicates of errors we already explicitly
        // report via addClientError / addHttpError. RPErrorsHandler's
        // console.error() call (Angular's default debug-logging behavior)
        // gets auto-captured by the Datadog SDK, creating a second event
        // for every unhandled error. Filtering at the SDK boundary keeps
        // event counts accurate without disabling the dev-mode console
        // experience. Side effect: legitimate console.error() calls from
        // third-party libraries are also dropped — rarely actionable; add
        // an allowlist exception here if a specific library's console
        // output ever becomes important.
        if (event.error?.source === 'console') {
          return false;
        }

        const message = event.error?.message ?? '';

        // Drop benign browser interventions and cross-origin script load
        // failures. These come from outside our code (the browser, third-
        // party scripts) and are never actionable from RUM.
        if (
          message.includes(
            'ResizeObserver loop completed with undelivered notifications'
          ) ||
          message.includes(
            'intervention: Ignored attempt to cancel a touchmove'
          ) ||
          message.includes('intervention: Blocked attempt to show a ') ||
          message === 'Script error.' ||
          message.startsWith('Uncaught "Script error.') ||
          IE_EDGE_ACTIVEX_PATTERN.test(message)
        ) {
          return false;
        }

        // Custom fingerprint for HTTP errors so each (method, status, urlPath,
        // backendType) becomes its own RUM issue. Without this, every HTTP
        // error groups by the synthetic stack frame at addHttpError and
        // collapses unrelated backend failures into the same bucket. Including
        // backendType (e.g. SqlException, NullReferenceException) splits same-
        // endpoint failures by root cause when the backend provides it.
        const ctx = event.context as Record<string, unknown> | undefined;
        if (ctx?.['type'] === 'HttpError' && event.error) {
          const method = String(ctx['method'] ?? '');
          const status = String(ctx['status'] ?? '');
          const url = String(ctx['url'] ?? '');
          const urlPath = url.split('?')[0];
          const backendType = ctx['backendType'];
          const typeSuffix =
            typeof backendType === 'string' && backendType.length > 0
              ? `:${backendType}`
              : '';
          event.error.fingerprint = `HttpError:${method}:${status}:${urlPath}${typeSuffix}`;
        }

        return true;
      }
    });

    // Add API environment as custom context
    datadogRum.setGlobalContextProperty(
      'api_env',
      this.envConfig.currentApiEnvironment
    );
  }

  setUser(
    user: UserInfoResultDto,
    isImpersonated: boolean = false,
    originalUserId?: string
  ): void {
    // Set user context - the individual person with custom properties
    datadogRum.setUser({
      id: user.userId,
      name: user.displayName,
      email: user.email,
      // Custom properties for filtering and segmentation
      isInternal: user.isInternal,
      isPortalAdmin: user.isPortalAdmin,
      primaryBusinessRole: user.primaryBusinessRole,
      primaryDepartmentId: user.primaryDepartmentId,
      memberOfDepartmentIdCSV: user.memberOfDepartmentIdCSV,
      canInvoiceOwner: user.canInvoiceOwner,
      canImpersonate: user.canImpersonate,
      // Impersonation context
      ...(isImpersonated && originalUserId
        ? {
            originalUserId,
            isImpersonated
          }
        : {})
    });

    // Set account context - the organization/company the user belongs to
    if (user.companyId && user.companyName) {
      datadogRum.setAccount({
        id: user.companyId.toString(),
        name: user.companyName
      });
    }
  }

  clearUser(): void {
    datadogRum.clearUser();
    datadogRum.clearAccount();
  }

  /**
   * Reports a non-HTTP client-side error (Angular Error, ReferenceError, …)
   * to Datadog RUM. HTTP errors must go through addHttpError — which the
   * DatadogErrorInterceptor calls so both caught and uncaught HTTP failures
   * reach RUM without double-counting from error handlers.
   */
  addClientError(error: Error): void {
    datadogRum.addError(error, {
      source: 'client',
      type: error.constructor.name,
      message: error.message,
      stack: error.stack
    });
  }

  /**
   * Reports an HttpErrorResponse to Datadog RUM with a concise headline and
   * structured context. Wrapping the response in a real Error lets RUM
   * capture a stack trace from the current call site (i.e., the interceptor
   * path) instead of showing "No stack, consider using an instance of Error".
   * The rich details — backend payload, status line, HTTP method, backend
   * exception type — go into the context so they're searchable as attributes
   * and so beforeSend can use backendType to refine the issue fingerprint.
   *
   * Drops events that represent expected business outcomes (status 0
   * cancellations, 401 anonymous-user responses, and resident-ui's wrong-
   * credential 404 on /auth/login). See shouldDropHttpError for rationale
   * per rule.
   */
  addHttpError(error: HttpErrorResponse, httpMethod: string): void {
    if (shouldDropHttpError(error, httpMethod)) return;

    const {
      message: backendMessage,
      detail: backendDetail,
      type: backendType
    } = extractBackendErrorFields(error.error);
    const urlWithoutQuery = error.url?.split('?')[0];
    const headline = [
      'HTTP',
      httpMethod,
      String(error.status),
      error.statusText,
      urlWithoutQuery
    ]
      .filter(Boolean)
      .join(' ');

    const wrapped = new Error(headline);
    wrapped.name = HttpErrorResponse.name;

    // Auth-adjacent endpoints: drop backend body text and exception type to
    // prevent account enumeration via response-shape differences (e.g.
    // distinguishing "user not found" from "invalid password" by exception
    // class). Status + URL still reach RUM, so aggregate auth-failure
    // monitoring still works.
    const authPath = isAuthPath(error.url);

    datadogRum.addError(wrapped, {
      source: 'server',
      type: 'HttpError',
      method: httpMethod,
      status: error.status,
      statusText: error.statusText,
      url: error.url,
      backendMessage: authPath ? null : sanitizeBackendField(backendMessage),
      backendDetail: authPath ? null : sanitizeBackendField(backendDetail),
      backendType: authPath ? null : backendType
    });
  }
}

// ---- Helpers --------------------------------------------------------------
// Ordered by importance for the reader: filter rules first, then backend-
// body parsing, then sanitization, then path detection.

/**
 * Decide whether an HttpErrorResponse should be dropped before reporting to
 * Datadog RUM. Each rule represents an expected business outcome that is not
 * actionable at the RUM layer:
 *
 * - status 0: browser-aborted / offline / CORS / navigation cancellation.
 *   This also catches mid-flight cancels of programmatically-fetched assets
 *   (e.g. inline SVG fetches); genuine asset 4xx/5xx still reach RUM so
 *   missing-asset deploy bugs stay visible.
 * - status 401: anonymous user pre-auth, or expired session — handled by the
 *   app's auth flow, not a signal of an application bug.
 * - 404 POST /auth/login: wrong-credential attempts. identity-api currently
 *   returns 404 for this case (a non-standard convention — RFC 7235 calls
 *   for 401). Tracked for backend correction in DX-1071; once the backend
 *   returns 401, the rule above will catch it and this special case can be
 *   removed.
 */
function shouldDropHttpError(
  error: HttpErrorResponse,
  httpMethod: string
): boolean {
  if (error.status === 0) return true;
  if (error.status === 401) return true;
  if (
    error.status === 404 &&
    httpMethod === 'POST' &&
    error.url?.endsWith('/auth/login')
  ) {
    return true;
  }
  return false;
}

/**
 * Best-effort extraction of the structured error fields that management-api
 * returns via MPExceptionHelper. Accepts plain strings, PascalCase, and
 * camelCase because different controllers have historically returned different
 * shapes. Returns null fields when nothing usable is present.
 *
 * The `type` field (extracted from ExceptionType / exceptionType) feeds into
 * the RUM fingerprint in beforeSend so two distinct exception classes hitting
 * the same endpoint produce two distinct RUM issues, not one collapsed bucket.
 */
function extractBackendErrorFields(body: unknown): {
  message: string | null;
  detail: string | null;
  type: string | null;
} {
  if (body === null || body === undefined) {
    return { message: null, detail: null, type: null };
  }
  if (typeof body === 'string') {
    return { message: body, detail: null, type: null };
  }
  // Non-object primitives (number/boolean/bigint/symbol) are not realistic
  // shapes for HttpErrorResponse.error; they would fall through the object
  // branch below and return null fields via the typeof guards.
  const obj = body as Record<string, unknown>;
  const rawMessage = obj['Message'] ?? obj['message'];
  const rawDetail =
    obj['MessageDetail'] ??
    obj['messageDetail'] ??
    obj['Detail'] ??
    obj['detail'];
  const rawType = obj['ExceptionType'] ?? obj['exceptionType'];
  return {
    message: typeof rawMessage === 'string' ? rawMessage : null,
    detail: typeof rawDetail === 'string' ? rawDetail : null,
    type: typeof rawType === 'string' ? rawType : null
  };
}

/**
 * Truncate long strings and redact credential-shaped tokens before the value
 * is shipped to Datadog RUM. Applied to backendMessage / backendDetail to cap
 * event size (Datadog's per-attribute soft limit is ~1 KiB) and to prevent
 * accidental leakage of bearer tokens / JWTs echoed in error payloads.
 */
function sanitizeBackendField(s: string | null): string | null {
  if (s == null) return null;
  const redacted = s
    // Email redaction is intentionally disabled. Emails are already logged
    // directly on errors and we rely on them for user correlation in Datadog.
    // Enable if a future privacy review requires it.
    // .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '<email>')
    .replace(/Bearer\s+[A-Za-z0-9._-]+/gi, 'Bearer <token>')
    .replace(/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '<jwt>');
  return redacted.length > MAX_BACKEND_FIELD_LEN
    ? redacted.slice(0, MAX_BACKEND_FIELD_LEN) + '…[truncated]'
    : redacted;
}

function isAuthPath(url: string | null | undefined): boolean {
  if (!url) return false;
  return AUTH_PATH_PATTERNS.some((p) => url.includes(p));
}
